<template>
  <div>
    <div class="page-header">
      <button class="btn btn-secondary back-button" @click="goBack">
        <i class="fa fa-arrow-left"></i> Back
      </button>
      <h4 class="table-page-header authorization-title">
        {{ clientName }}, {{ authorizationNumber }}
      </h4>
    </div>

    <!-- Monthly Invoices Section -->
    <div class="authorization-content">
      <div class="section-header">
        <h5>Monthly Invoices</h5>
        <div class="actions">
          <button
            v-if="monthlyInvoices.length > 0"
            class="btn btn-sm btn-secondary"
            @click="toggleAllInvoices"
          >
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="text-center">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p>Loading invoices...</p>
      </div>

      <div v-else-if="monthlyInvoices.length === 0" class="no-data">
        <p>No approved shifts found for this authorization</p>
      </div>

      <div v-else class="invoices-list">
        <div
          v-for="invoice in monthlyInvoices"
          :key="invoice.monthKey"
          class="invoice-card"
        >
          <div class="invoice-header" @click="viewInvoiceDetails(invoice)">
            <div class="invoice-info">
              <h6 class="invoice-month">{{ invoice.monthDisplay }}</h6>
              <div class="invoice-stats">
                <span class="stat">
                  <i class="fa fa-file-invoice"></i>
                  {{ invoice.shifts.length }} shift{{ invoice.shifts.length !== 1 ? 's' : '' }}
                </span>
                <span class="stat">
                  <i class="fa fa-clock"></i>
                  {{ invoice.totalHours }} hours
                </span>
              </div>
            </div>
            <button
              class="btn btn-sm btn-link expand-toggle"
              @click.stop="toggleInvoice(invoice.monthKey)"
            >
              <i :class="invoice.expanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
            </button>
          </div>

          <div v-if="invoice.expanded" class="invoice-shifts">
            <table class="shifts-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Caregiver</th>
                  <th>Service Code</th>
                  <th>Clock In</th>
                  <th>Clock Out</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="shift in invoice.shifts" :key="shift.id">
                  <td>{{ formatShiftDate(shift.scheduled_start) }}</td>
                  <td>{{ formatCaregivers(shift.caregivers) }}</td>
                  <td>{{ formatServiceCodes(shift.service_codes) }}</td>
                  <td>{{ formatTime(shift.clock_in) }}</td>
                  <td>{{ formatTime(shift.clock_out) }}</td>
                  <td>{{ shift.hours_applied_to_auth || shift.billing_hours || '0.00' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { shiftsService } from "@/serviceHandlers/shiftsService";

export default {
  name: "AuthorizationDetails",
  data() {
    return {
      authorizationId: null,
      authorizationNumber: "",
      clientId: null,
      clientName: "",
      loading: false,
      monthlyInvoices: [],
      allExpanded: false,
    };
  },
  async mounted() {
    this.authorizationId = this.$route.params.id;
    this.authorizationNumber = this.$route.query.authNumber || "Unknown";
    // clientId can be in params (from ClientAuthorizationDetails) or query (from regular AuthorizationDetails)
    this.clientId = this.$route.params.clientId || this.$route.query.clientId;
    this.clientName = this.$route.query.clientName;

    await this.loadShifts();
  },
  methods: {
    async loadShifts() {
      this.loading = true;
      try {
        const response = await shiftsService.getApprovedShiftsByAuthorization(
          this.authorizationId
        );

        if (response && response.shifts) {
          this.groupShiftsByMonth(response.shifts);
        }
      } catch (error) {
        this.$notifyError("Error loading shifts. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    groupShiftsByMonth(shifts) {
      const groupedShifts = {};

      shifts.forEach((shift) => {
        if (!shift.scheduled_start) return;

        const date = new Date(shift.scheduled_start);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!groupedShifts[monthKey]) {
          groupedShifts[monthKey] = {
            monthKey,
            monthDisplay: date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long'
            }),
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            shifts: [],
            totalHours: 0,
            expanded: false,
          };
        }

        groupedShifts[monthKey].shifts.push(shift);
        const hours = parseFloat(shift.hours_applied_to_auth || shift.billing_hours || 0);
        groupedShifts[monthKey].totalHours += hours;
      });

      // Convert to array and sort by date (most recent first)
      this.monthlyInvoices = Object.values(groupedShifts)
        .map(invoice => ({
          ...invoice,
          totalHours: invoice.totalHours.toFixed(2),
        }))
        .sort((a, b) => {
          if (b.year !== a.year) return b.year - a.year;
          return b.month - a.month;
        });
    },

    toggleInvoice(monthKey) {
      const invoice = this.monthlyInvoices.find(inv => inv.monthKey === monthKey);
      if (invoice) {
        invoice.expanded = !invoice.expanded;
      }
    },

    toggleAllInvoices() {
      this.allExpanded = !this.allExpanded;
      this.monthlyInvoices.forEach(invoice => {
        invoice.expanded = this.allExpanded;
      });
    },

    viewInvoiceDetails(invoice) {
      // Check if we're in a client context
      const isClientContext = this.$route.name === "ClientAuthorizationDetails";
      const routeName = isClientContext ? "ClientInvoiceDetails" : "InvoiceDetails";

      const params = {
        authorizationId: this.authorizationId,
        year: invoice.year,
        month: invoice.month
      };

      // Add clientId if in client context
      if (isClientContext) {
        params.clientId = this.$route.params.clientId;
      }

      this.$router.push({
        name: routeName,
        params,
        query: {
          authNumber: this.authorizationNumber,
          clientName: this.clientName,
          monthDisplay: invoice.monthDisplay,
          // Keep the origin so back from invoices → here → origin
          from: this.$route.query.from,
        },
      });
    },

    formatShiftDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatCaregivers(caregivers) {
      if (!caregivers || caregivers.length === 0) return 'N/A';
      return caregivers.map(c => c.name).join(', ');
    },

    formatServiceCodes(serviceCodes) {
      if (!serviceCodes || serviceCodes.length === 0) return 'N/A';
      return serviceCodes.map(sc => sc.code).join(', ');
    },

    goBack() {
      // The Authorizations list marks its navigations with ?from= — both
      // entry points use the same client-scoped route, so the route name
      // alone can't tell where the user came from
      if (this.$route.query.from === "authorizations") {
        this.$router.push({ name: "Authorizations" });
        return;
      }

      // Check if we're in a client context by checking the route name
      const isClientContext = this.$route.name === "ClientAuthorizationDetails";

      if (isClientContext && this.clientId) {
        // Navigate back to the client details page
        this.$router.push({
          name: "ClientDetails",
          params: { id: this.clientId },
          query: { name: this.clientName },
        });
      } else {
        // Fallback to Authorizations list or browser back
        this.$router.push({ name: "Authorizations" });
      }
    },
  },
};
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.back-button {
  font-size: 14px;
  padding: 8px 16px;
  height: 40px;
}

.back-button i {
  margin-right: 6px;
}

.authorization-title {
  margin: 0;
  flex: 0 0 auto;
}

.authorization-content {
  padding: 1.5rem;
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.section-header h5 {
  margin: 0;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
}

.section-header .actions {
  display: flex;
  gap: 0.5rem;
}

.text-center {
  text-align: center;
  padding: 3rem 0;
  color: rgba(255, 255, 255, 0.7);
}

.text-center i {
  color: #66ccff;
  margin-bottom: 1rem;
}

.text-center p {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 1rem;
}

.no-data {
  text-align: center;
  padding: 3rem 0;
  color: rgba(255, 255, 255, 0.5);
}

.no-data p {
  margin: 0;
  font-size: 1rem;
}

.invoices-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invoice-card {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.invoice-card:hover {
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.invoice-header:hover {
  background: rgba(255, 255, 255, 0.03);
}

.invoice-info {
  flex: 1;
}

.invoice-month {
  margin: 0 0 0.5rem 0;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
}

.invoice-stats {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

.stat i {
  color: #66ccff;
}

.expand-toggle {
  padding: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.2s ease;
}

.expand-toggle:hover {
  color: #fff;
  background: transparent;
}

.invoice-shifts {
  padding: 0 1.25rem 1.25rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 2000px;
  }
}

.shifts-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.shifts-table thead {
  background: rgba(255, 255, 255, 0.05);
}

.shifts-table th {
  padding: 0.75rem;
  text-align: left;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.shifts-table td {
  padding: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.shifts-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.shifts-table tbody tr:last-child td {
  border-bottom: none;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .invoice-stats {
    flex-direction: column;
    gap: 0.5rem;
  }

  .shifts-table {
    font-size: 0.85rem;
  }

  .shifts-table th,
  .shifts-table td {
    padding: 0.5rem 0.25rem;
  }
}
</style>
